import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Container, Row, Col, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { API_ENDPOINTS } from '../../config/properties';
import authService from '../../services/authService';
import './SuperEnalottoExtractionHistory.css';

/**
 * Component to display SuperEnalotto extraction history with date range filtering
 */
const SuperEnalottoExtractionHistory = () => {
  const { t } = useTranslation();
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(50);

  // Fetch extractions by date range (or all if no dates specified)
  const fetchExtractionsByDateRange = async () => {
    // Validate date range only if both dates are provided
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      setError(t('invalidDateRange'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let url;

      // If both dates are provided, use date range endpoint
      if (fromDate && toDate) {
        url = `${API_ENDPOINTS.superEnalottoExtractionsByDateRange}?from=${fromDate}&to=${toDate}&limit=${limit}`;
      } else {
        // Otherwise, use general extractions endpoint (get latest)
        url = `${API_ENDPOINTS.superEnalottoExtractions}?limit=${limit}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: authService.getAuthHeader(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setExtractions(data);
    } catch (err) {
      console.error('Error fetching extractions:', err);
      setError(t('errorFetchingData') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFromDate('');
    setToDate('');
    setExtractions([]);
    setError(null);
  };

  // Format numbers for display
  const formatNumbers = (extraction) => {
    if (!extraction.numbers || extraction.numbers.length === 0) {
      return '-';
    }
    return extraction.numbers.join(', ');
  };

  return (
    <Container fluid className="superenalotto-extraction-history py-4">
      <Row className="mb-4">
        <Col md={12}>
          <h5 className="text-primary mb-3">
            <i className="bi bi-calendar-event"></i> {t('extractionHistory')}
          </h5>

          {/* Search Form */}
          <div className="card card-search mb-4">
            <div className="card-body">
              <Form>
                <Row className="mb-3">
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('fromDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('toDate')}</Form.Label>
                      <Form.Control
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label className="fw-bold">{t('limit')}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="1000"
                        value={limit}
                        onChange={(e) => setLimit(parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} sm={6} xs={12} className="mb-2">
                    <Form.Group>
                      <Form.Label>&nbsp;</Form.Label>
                      <div className="d-flex gap-2">
                        <Button
                          variant="primary"
                          onClick={fetchExtractionsByDateRange}
                          disabled={loading}
                          className="flex-grow-1"
                        >
                          {loading ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              {t('searching')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-search"></i> {t('search')}
                            </>
                          )}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          <i className="bi bi-arrow-counterclockwise"></i> {t('reset')}
                        </Button>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              <i className="bi bi-exclamation-triangle"></i> {error}
            </Alert>
          )}

          {/* Results Table */}
          {extractions.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover className="extraction-table">
                <thead className="table-header">
                  <tr>
                    <th className="text-center">ID</th>
                    <th className="text-center">{t('extractionDate')}</th>
                    <th className="text-center">{t('numbers')} (6)</th>
                    <th className="text-center" style={{ backgroundColor: '#ffc107', color: '#000' }}>
                      Jolly <i className="bi bi-star-fill"></i>
                    </th>
                    <th className="text-center" style={{ backgroundColor: '#28a745', color: '#fff' }}>
                      SuperStar ⭐
                    </th>
                    <th className="text-center">{t('totalWinners')}</th>
                    <th className="text-center">{t('totalAmount')}</th>
                    <th className="text-center">Cat. 1</th>
                    <th className="text-center">Cat. 2</th>
                    <th className="text-center">Cat. 3</th>
                  </tr>
                </thead>
                <tbody>
                  {extractions.map((extraction, index) => (
                    <tr key={extraction.id || index}>
                      <td className="text-center text-muted">{extraction.id}</td>
                      <td className="text-center">{extraction.extractionDate}</td>
                      <td className="text-center">
                        <span className="badge badge-numbers">{formatNumbers(extraction)}</span>
                      </td>
                      <td className="text-center">
                        <Badge bg="warning" text="dark" className="fs-6">
                          {extraction.jollyNumber || '-'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="success" className="fs-6">
                          {extraction.superstarNumber || '-'}
                        </Badge>
                      </td>
                      <td className="text-center fw-bold">{extraction.totalWinners || 0}</td>
                      <td className="text-end">€ {extraction.totalAmount?.toFixed(2) || '0.00'}</td>
                      <td className="text-center">
                        <span className="badge bg-danger">
                          {extraction.cat1Winners || 0}
                          {extraction.cat1Amount && (
                            <><br />€ {extraction.cat1Amount.toFixed(2)}</>
                          )}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-warning text-dark">
                          {extraction.cat2Winners || 0}
                          {extraction.cat2Amount && (
                            <><br />€ {extraction.cat2Amount.toFixed(2)}</>
                          )}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-info">
                          {extraction.cat3Winners || 0}
                          {extraction.cat3Amount && (
                            <><br />€ {extraction.cat3Amount.toFixed(2)}</>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : !loading && extractions.length === 0 && !error && (fromDate || toDate) ? (
            <Alert variant="info">
              <i className="bi bi-info-circle"></i> {t('noResourcesFound')}
            </Alert>
          ) : null}

          {/* Loading Indicator */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">{t('loading')}</span>
              </Spinner>
              <p className="mt-3 text-muted">{t('loadingData')}</p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SuperEnalottoExtractionHistory;
